/**
 * Settings > Dunning (tracker item 36): automatic overdue reminders.
 *
 * The mode (off / on), the start date, the hour of the daily batch, the
 * minimum days between two reminders to one customer, and the reminder
 * schedule: up to six steps, each a number of days overdue the office types,
 * switched on or off, with its own email template (Settings > Notifications).
 * ADMIN only (backend requireRole('ADMIN') on PUT /company-settings and
 * PUT /dunning/steps).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, ICompanySettingsApi, IDunningApi, INotificationsApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import {
  DUNNING_PATH,
  MAX_DUNNING_STEPS,
  MAX_STEP_DAYS,
  MIN_STEP_DAYS,
  StepDraft,
  dunningError,
  newStepDraft,
  sendingWarning,
  templatePath,
  toReplaceStepsRequest,
  toStepDrafts,
  validateStepDrafts,
} from '../dunningUi';
import { Toast } from '../../components/Toast';

type Api = ICompanySettingsApi & IDunningApi & INotificationsApi;

interface ModeForm {
  mode: types.DunningMode;
  startDate: string;
  hour: string;
  minDays: string;
}

function toModeForm(settings: types.CompanySettings): ModeForm {
  return {
    mode: settings.dunningMode ?? 'OFF',
    startDate: settings.dunningStartDate ?? '',
    hour: String(settings.dunningHour ?? 9),
    minDays: String(settings.dunningMinDaysBetween ?? 7),
  };
}

/** Field problems of the mode form ({} = fine). */
export function validateModeForm(form: ModeForm): Partial<Record<keyof ModeForm, string>> {
  const errors: Partial<Record<keyof ModeForm, string>> = {};
  if (!/^\d+$/.test(form.hour.trim()) || Number(form.hour) > 23) errors.hour = 'A whole hour from 0 to 23';
  if (!/^\d+$/.test(form.minDays.trim()) || Number(form.minDays) < 1 || Number(form.minDays) > 365) errors.minDays = 'A whole number of days from 1 to 365';
  return errors;
}

export function DunningSettingsPage() {
  const api = useMemo<Api>(() => createAPIClient(), []);
  const [settings, setSettings] = useState<types.CompanySettings | null>(null);
  const [notification, setNotification] = useState<types.NotificationSettings | null>(null);
  const [form, setForm] = useState<ModeForm | null>(null);
  const [drafts, setDrafts] = useState<StepDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [company, steps, sending] = await Promise.all([
        api.getCompanySettings(),
        api.getDunningSteps(),
        api.getNotificationSettings().catch(() => null),
      ]);
      setSettings(company);
      setForm(toModeForm(company));
      setDrafts(toStepDrafts(steps.steps));
      setNotification(sending);
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not load the dunning settings'));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  const modeErrors = form ? validateModeForm(form) : {};
  const stepErrors = validateStepDrafts(drafts);
  const warning = sendingWarning(notification);

  async function saveMode(event: React.FormEvent) {
    event.preventDefault();
    if (!settings || !form || Object.keys(modeErrors).length > 0) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const request: types.UpdateCompanySettingsRequest = {
        companyName: settings.companyName,
        dunningMode: form.mode,
        dunningHour: Number(form.hour),
        dunningMinDaysBetween: Number(form.minDays),
      };
      // An empty field with no stored date is left out, so turning dunning on
      // starts it today (server default); null only clears a stored date
      if (form.startDate) request.dunningStartDate = form.startDate;
      else if (settings.dunningStartDate) request.dunningStartDate = null;
      const saved = await api.updateCompanySettings(request);
      setSettings(saved);
      setForm(toModeForm(saved));
      // Turning dunning on turns the step templates on: reload them
      setDrafts(toStepDrafts((await api.getDunningSteps()).steps));
      setNotice(saved.dunningMode === 'ON' ? 'Automatic reminders are on.' : 'Dunning settings saved.');
    } catch (cause) {
      setError(dunningError(cause, 'Could not save the dunning settings'));
    } finally {
      setBusy(false);
    }
  }

  async function saveSteps() {
    if (stepErrors.some(Boolean)) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await api.replaceDunningSteps(toReplaceStepsRequest(drafts));
      setDrafts(toStepDrafts(saved.steps));
      setNotice('Reminder schedule saved.');
    } catch (cause) {
      setError(dunningError(cause, 'Could not save the schedule'));
    } finally {
      setBusy(false);
    }
  }

  const update = (index: number, change: Partial<StepDraft>) =>
    setDrafts((list) => list.map((d, i) => (i === index ? { ...d, ...change } : d)));

  if (loading) return <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">Loading dunning settings…</main>;

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/settings" className="text-sm text-blue-700 hover:underline">← Settings</Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">Dunning (overdue reminders)</h1>
        </div>
        <Link to={DUNNING_PATH} className={secondaryButtonClass}>Preview and send</Link>
      </div>

      {error && <Toast kind="error">{error}</Toast>}
      {notice && <Toast kind="success">{notice}</Toast>}
      {warning && <p role="note" className="rounded bg-yellow-50 p-3 text-sm text-yellow-900">{warning}</p>}

      <section aria-labelledby="dunning-rules-heading" className="bg-white rounded-lg shadow p-5 text-sm text-gray-700 space-y-2">
        <h2 id="dunning-rules-heading" className="font-semibold text-lg text-gray-900">How reminders work</h2>
        <p>Each step is a number of days after an invoice's due date. When an invoice reaches a step, its customer gets a reminder.</p>
        <p>
          <strong>One email per customer:</strong> a customer with several overdue invoices gets one email listing all of them, using the
          template of the highest step any of them has reached.
        </p>
        <p>
          <strong>Once per step:</strong> an invoice is reminded at most once at each step. If a run was missed, only the highest step it
          has now reached is sent; the earlier ones are skipped.
        </p>
        <p>
          Held invoices (on the invoice page) and customers excluded from reminders (on the customer page) are left out. Reminders never
          add late fees.
        </p>
      </section>

      {form && (
        <form onSubmit={(e) => void saveMode(e)} aria-labelledby="dunning-mode-heading" className="bg-white rounded-lg shadow p-5 space-y-4">
          <h2 id="dunning-mode-heading" className="font-semibold text-lg">Automatic reminders</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="dunning-mode" className={labelClass}>Mode</label>
              <select id="dunning-mode" className={inputClass} value={form.mode} disabled={busy}
                onChange={(e) => setForm({ ...form, mode: e.target.value as types.DunningMode })}>
                <option value="OFF">Off: only when sent by hand</option>
                <option value="ON">On: once a day, automatically</option>
              </select>
            </div>
            <div>
              <label htmlFor="dunning-start" className={labelClass}>Start date</label>
              <input id="dunning-start" type="date" className={inputClass} value={form.startDate} disabled={busy}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })} aria-describedby="dunning-start-hint" />
              <p id="dunning-start-hint" className="mt-1 text-xs text-gray-500">
                Only invoices due on or after this date get reminders. Set to today when reminders are first turned on.
              </p>
            </div>
            <div>
              <label htmlFor="dunning-hour" className={labelClass}>Daily run hour (UTC)</label>
              <input id="dunning-hour" inputMode="numeric" className={inputClass} value={form.hour} disabled={busy}
                onChange={(e) => setForm({ ...form, hour: e.target.value })} aria-invalid={!!modeErrors.hour} />
              {modeErrors.hour && <p className="mt-1 text-xs text-red-700">{modeErrors.hour}</p>}
            </div>
            <div>
              <label htmlFor="dunning-min-days" className={labelClass}>Minimum days between reminders</label>
              <input id="dunning-min-days" inputMode="numeric" className={inputClass} value={form.minDays} disabled={busy}
                onChange={(e) => setForm({ ...form, minDays: e.target.value })} aria-invalid={!!modeErrors.minDays} />
              {modeErrors.minDays && <p className="mt-1 text-xs text-red-700">{modeErrors.minDays}</p>}
            </div>
          </div>
          <button type="submit" className={primaryButtonClass} disabled={busy || Object.keys(modeErrors).length > 0}>Save</button>
        </form>
      )}

      <section aria-labelledby="dunning-steps-heading" className="bg-white rounded-lg shadow p-5">
        <h2 id="dunning-steps-heading" className="font-semibold text-lg mb-1">Reminder schedule</h2>
        <p className="text-sm text-gray-600 mb-4">
          Type the number of days overdue for each step ({MIN_STEP_DAYS} to {MAX_STEP_DAYS}). Steps are ordered by days when saved. Up to{' '}
          {MAX_DUNNING_STEPS} steps; each has its own email in Settings &gt; Notifications.
        </p>
        <table className="responsive-table min-w-full text-sm">
          <caption className="sr-only">Reminder steps</caption>
          <thead>
            <tr className="border-b text-left text-gray-600">
              <th scope="col" className="py-2 pr-4 font-medium">Step</th>
              <th scope="col" className="py-2 pr-4 font-medium">Days overdue</th>
              <th scope="col" className="py-2 pr-4 font-medium">On</th>
              <th scope="col" className="py-2 pr-4 font-medium">Email template</th>
              <th scope="col" className="py-2 font-medium"><span className="sr-only">Remove</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {drafts.length === 0 && (
              <tr><td colSpan={5} className="py-4 text-gray-600">No steps: no reminders are sent.</td></tr>
            )}
            {drafts.map((draft, index) => (
              <tr key={draft.eventKey ?? `new-${index}`}>
                <td data-label="Step" className="py-2 pr-4">{index + 1}</td>
                <td data-label="Days overdue" className="py-2 pr-4">
                  <div className="flex-1 min-w-0 flex flex-col items-end md:block">
                  <input
                    className={`${inputClass} w-24 max-w-[8rem] md:max-w-none text-base sm:text-sm`}
                    inputMode="numeric"
                    value={draft.days}
                    disabled={busy}
                    aria-label={`Days overdue for step ${index + 1}`}
                    aria-invalid={!!stepErrors[index]}
                    onChange={(e) => update(index, { days: e.target.value })}
                  />
                  {stepErrors[index] && <p className="mt-1 text-xs text-red-700 text-right md:text-left">{stepErrors[index]}</p>}
                  </div>
                </td>
                <td data-label="On" className="py-2 pr-4">
                  <input type="checkbox" className="h-5 w-5 md:h-auto md:w-auto" checked={draft.isActive} disabled={busy} aria-label={`Step ${index + 1} on`}
                    onChange={(e) => update(index, { isActive: e.target.checked })} />
                </td>
                <td data-label="Email template" className="py-2 pr-4">
                  {draft.eventKey ? (
                    <div className="min-w-0 break-words text-right md:text-left">
                      <Link to={templatePath(draft.eventKey)} className="text-blue-700 hover:underline">{draft.templateLabel ?? draft.eventKey}</Link>
                      {draft.templateActive === false && <span className="block text-xs text-amber-700">Template is off</span>}
                    </div>
                  ) : (
                    <span className="text-gray-500">Assigned when saved</span>
                  )}
                </td>
                <td className="py-2 text-right">
                  <button type="button" className="text-sm text-red-700 hover:underline disabled:opacity-50 min-h-[44px] md:min-h-0" disabled={busy}
                    onClick={() => setDrafts((list) => list.filter((_, i) => i !== index))}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button type="button" className={secondaryButtonClass} disabled={busy || drafts.length >= MAX_DUNNING_STEPS}
            onClick={() => setDrafts((list) => [...list, newStepDraft(list)])}>
            Add step
          </button>
          {drafts.length >= MAX_DUNNING_STEPS && <span className="text-xs text-gray-500">All {MAX_DUNNING_STEPS} steps are used: remove one to add another.</span>}
          <button type="button" className={primaryButtonClass} disabled={busy || stepErrors.some(Boolean)} onClick={() => void saveSteps()}>
            Save schedule
          </button>
        </div>
      </section>
    </main>
  );
}
